import { formatShortDate } from '@packages/format';
import { projectBalanceSheetLayout } from './balance-sheet-presentation.js';
import { asOfSeriesColumns, asOfSeriesPdfFits, mergeSeriesAccounts, mergeSeriesGroupNodes, } from './series-presentation.js';
/**
 * One column per date the series states a position at, in the order the series
 * returns them (oldest first, anchor last).
 *
 * Labelled by DATE, never by month name: a column's date is not always a month
 * end - the anchor can be any day - and "August 2026" over a column dated the
 * 2nd would misstate what the figures are as at.
 *
 * `separator` draws a rule wherever a column is NOT the month end immediately
 * following its neighbour, which happens in two ways and is wanted in both. A
 * `prior-year` comparison jumps a year. A mid-month anchor ends a run of month
 * ends with a partial month - "30 Jun, 31 Jul, 31 Aug, 3 Sep" - and the rule is
 * what stops the last column reading as another completed month. A series whose
 * anchor IS a month end is one unbroken walk and carries no rule at all.
 */
export function balanceSheetSeriesColumns(series) {
    return asOfSeriesColumns(series.columns, formatShortDate);
}
export function collectBalanceSheetSeriesGroupIds(rows) {
    const ids = [];
    const visit = (node) => {
        ids.push(node.id);
        node.groups.forEach(visit);
    };
    for (const row of rows) {
        if (row.kind === 'section')
            row.section?.groups.forEach(visit);
    }
    return ids;
}
/**
 * The series projection: the same walk the single-date view makes, with every
 * figure read across the columns instead of off one statement.
 *
 * Returned flat (body then footer) because the series table renders one
 * `<tbody>`; the walk keeps them apart for the screen, which needs a `<tfoot>`.
 */
export function buildBalanceSheetSeriesTableRows(columns) {
    const { body, footer } = projectBalanceSheetLayout({
        sectionFor: (key) => {
            const sectionsByColumn = columns.map((column) => column.statement.sections.find((s) => s.section === key));
            const present = sectionsByColumn.find((section) => section !== undefined);
            if (!present)
                return undefined;
            // A column the section is absent from held nothing in it, which on a
            // statement is a figure of zero rather than a blank.
            const subtotalMinorUnits = sectionsByColumn.map((section) => section?.subtotalMinorUnits ?? 0);
            return {
                section: {
                    groups: mergeSeriesGroupNodes(sectionsByColumn.map((section) => section?.groups ?? [])),
                    accounts: mergeSeriesAccounts(sectionsByColumn.map((section) => section?.accounts ?? [])),
                    subtotalMinorUnits,
                },
                label: present.label,
                subtotalMinorUnits,
            };
        },
        field: (name) => columns.map((column) => column.statement[name]),
        labelFor: () => columns[0]?.statement.accumulatedProfitAccountName ?? null,
        sum: (amounts) => columns.map((_column, index) => amounts.reduce((total, amount) => total + amount[index], 0)),
        isZero: (amount) => amount.every((figure) => figure === 0),
    });
    return [...body, ...footer];
}
/**
 * The balance sheet's name for the shared as-at series page-fit check, kept
 * because it is what its frontend was written against. An alias, not a second
 * definition, so the three as-at series can never disagree about how many
 * columns fit a page.
 */
export const balanceSheetSeriesPdfFits = asOfSeriesPdfFits;
